/** The binding mark next to a language leaf in the tag data editor (metaplan A6):
 *  "auto-translated from <lang>" on a target leaf, "→ fr, de" on an origin leaf. */
import { Chip, Tooltip } from '@mui/material';

import type { TranslationBinding } from '@/api/tags';
import { formatRelativeTime } from '@/utils/format';

import type { LeafBindings } from './translationBindings';

const PROBLEM: Record<string, string> = {
  error: 'The last re-translation failed',
  paused: 'Paused — not re-translated',
  orphaned: 'Orphaned — a bound leaf is gone',
};

function targetTooltip(b: TranslationBinding): string {
  const parts = [`Auto-translated from ${b.origin_label}. Editing the origin re-translates this text.`];
  if (b.status !== 'active') parts.push(`${PROBLEM[b.status] ?? b.status}${b.last_error ? `: ${b.last_error}` : ''}.`);
  if (b.last_synced_ts) parts.push(`Last translated ${formatRelativeTime(b.last_synced_ts)}.`);
  return parts.join(' ');
}

export function TranslationBindingChip({ leaf }: { leaf: LeafBindings | undefined }) {
  if (!leaf) return null;
  const target = leaf.asTarget;
  if (target) {
    return (
      <Tooltip title={targetTooltip(target)}>
        <Chip
          size="small"
          variant="outlined"
          color={target.status === 'active' ? 'info' : 'warning'}
          label={`auto from ${target.origin_lang}`}
          aria-label={`auto-translated from ${target.origin_label}`}
          sx={{ mt: 0.5 }}
        />
      </Tooltip>
    );
  }
  if (leaf.asOrigin.length) {
    const langs = leaf.asOrigin.map((b) => b.target_lang).join(', ');
    return (
      <Tooltip title={`Feeds the auto-translation of: ${leaf.asOrigin.map((b) => b.target_label).join('; ')}. Editing this text re-translates them about a minute after saving.`}>
        <Chip size="small" variant="outlined" label={`→ ${langs}`} aria-label={`feeds translations to ${langs}`} sx={{ mt: 0.5 }} />
      </Tooltip>
    );
  }
  return null;
}
